<template>
  <v2-card>
    <template #header>
      <h3 class="v2-heading-sm">
        Commercial Information
      </h3>
    </template>

    <div class="v2-profile-doc__grid">
      <!-- Company Name -->
      <div class="v2-profile-doc__field v2-profile-doc__field--full">
        <v2-input
          v-model="companyName"
          label="Company Name"
          placeholder="Enter company name"
          :disabled="isNotEntityAdmin"
        />
      </div>

      <!-- Trade License File -->
      <div class="v2-profile-doc__field">
        <label class="v2-profile-doc__label">Trade License</label>
        <v2-file-upload
          v-model="tradeLicenseFile"
          accept=".pdf,.jpg,.jpeg,.png"
          :placeholder="documentLocal.tradeLicense ? 'Trade License Uploaded' : 'Choose a file or drop it here...'"
          :disabled="isNotEntityAdmin"
        />
        <a
          v-if="documentLocal.tradeLicense"
          :href="documentLocal.tradeLicense"
          target="_blank"
          rel="noopener noreferrer"
          class="v2-profile-doc__file-link"
        >
          View current file
        </a>
      </div>

      <!-- Trade License Number -->
      <div class="v2-profile-doc__field">
        <v2-input
          v-model="tradeLicenseNumber"
          label="Trade License Number"
          placeholder="Enter trade license number"
          :disabled="isNotEntityAdmin"
        />
      </div>

      <!-- VAT Certificate File -->
      <div class="v2-profile-doc__field">
        <label class="v2-profile-doc__label">VAT Certificate</label>
        <v2-file-upload
          v-model="vatCertificateFile"
          accept=".pdf,.jpg,.jpeg,.png"
          :placeholder="documentLocal.vatCertificate ? 'VAT Certificate Uploaded' : 'Choose a file or drop it here...'"
          :disabled="isNotEntityAdmin"
        />
        <a
          v-if="documentLocal.vatCertificate"
          :href="documentLocal.vatCertificate"
          target="_blank"
          rel="noopener noreferrer"
          class="v2-profile-doc__file-link"
        >
          View current file
        </a>
      </div>

      <!-- VAT Certificate Number -->
      <div class="v2-profile-doc__field">
        <v2-input
          v-model="vatCertificateNumber"
          label="VAT Certificate Number"
          placeholder="Enter VAT certificate number"
          :disabled="isNotEntityAdmin"
        />
      </div>

      <!-- Trade License Expiry Date (Retailer only) -->
      <div
        v-if="isRetailer"
        class="v2-profile-doc__field"
      >
        <label class="v2-profile-doc__label">Trade License Expiry Date</label>
        <v2-date-picker
          v-model="tradeLicenseExpiryDate"
          placeholder="Select expiry date"
          :disabled="isNotEntityAdmin"
        />
      </div>

      <!-- Date of Incorporation (Retailer only) -->
      <div
        v-if="isRetailer"
        class="v2-profile-doc__field"
      >
        <label class="v2-profile-doc__label">Date of Incorporation</label>
        <v2-date-picker
          v-model="dateOfIncorporation"
          placeholder="Select incorporation date"
          :disabled="isNotEntityAdmin"
        />
      </div>

      <!-- Company Profile File -->
      <div class="v2-profile-doc__field">
        <label class="v2-profile-doc__label">Company Profile</label>
        <v2-file-upload
          v-model="companyProfileFile"
          accept=".pdf,.jpg,.jpeg,.png"
          :placeholder="documentLocal.companyProfile ? 'Company Profile Uploaded' : 'Choose a file or drop it here...'"
          :disabled="isNotEntityAdmin"
        />
        <a
          v-if="documentLocal.companyProfile"
          :href="documentLocal.companyProfile"
          target="_blank"
          rel="noopener noreferrer"
          class="v2-profile-doc__file-link"
        >
          View current file
        </a>
      </div>

      <!-- Others File -->
      <div class="v2-profile-doc__field">
        <label class="v2-profile-doc__label">Other Documents</label>
        <v2-file-upload
          v-model="othersFile"
          accept=".pdf,.jpg,.jpeg,.png"
          :placeholder="documentLocal.others ? 'Document Uploaded' : 'Choose a file or drop it here...'"
          :disabled="isNotEntityAdmin"
        />
        <a
          v-if="documentLocal.others"
          :href="documentLocal.others"
          target="_blank"
          rel="noopener noreferrer"
          class="v2-profile-doc__file-link"
        >
          View current file
        </a>
      </div>
    </div>

    <!-- Actions -->
    <div class="v2-profile-doc__actions">
      <v2-button
        variant="primary"
        :loading="loading"
        :disabled="loading || isNotEntityAdmin"
        @click="onSave"
      >
        Save changes
      </v2-button>
      <v2-button
        variant="outline"
        :disabled="loading"
        @click="onCancel"
      >
        Cancel
      </v2-button>
    </div>
  </v2-card>
</template>

<script>
import { watch } from '@vue/composition-api'
import { useProfileDocument } from '@/composables/useProfileDocument'
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2FileUpload from '@/components/v2/V2FileUpload.vue'
import V2DatePicker from '@/components/v2/V2DatePicker.vue'

export default {
  name: 'V2ProfileDocument',
  components: {
    V2Card,
    V2Button,
    V2Input,
    V2FileUpload,
    V2DatePicker,
  },
  props: {
    documentData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update-document'],
  setup(props, { emit }) {
    const pd = useProfileDocument()
    pd.initDocument(props.documentData)

    async function onSave() {
      const result = await pd.uploadDocuments()
      if (result?.success) {
        emit('update-document', pd.documentLocal.value)
      }
    }

    function onCancel() {
      pd.resetForm(props.documentData)
    }

    watch(
      () => props.documentData,
      newData => {
        if (newData) {
          pd.initDocument(newData)
        }
      },
    )

    return {
      ...pd,
      onSave,
      onCancel,
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-profile-doc {
  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-4);
    margin-top: var(--v2-space-4);
    margin-bottom: var(--v2-space-5);
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-1);

    &--full {
      grid-column: 1 / -1;
    }
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }

  &__file-link {
    font-size: 0.75rem;
    color: var(--v2-primary);
    text-decoration: underline;
    margin-top: var(--v2-space-1);

    &:hover {
      opacity: 0.8;
    }
  }

  &__actions {
    display: flex;
    gap: var(--v2-space-3);
  }

  @media (max-width: 640px) {
    &__grid {
      grid-template-columns: 1fr;
    }

    &__field--full {
      grid-column: 1;
    }
  }
}
</style>
